import { useEffect, useMemo, useState } from "react";
import { CalendarDays, TrendingUp, Sparkles, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";

export default function PromoForecast() {
  const [data, setData] = useState({ days: [], forecast: [], summary: { avg_daily: 0, next_30d: 0 } });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/aff-panel/forecast");
      setData(r.data || {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const history = (data.days || []).map((d) => ({
    label: d.date.slice(5),
    commission: d.commission,
    deposits: d.deposits,
  }));
  const forecast = (data.forecast || []).map((d) => ({
    label: d.date.slice(5),
    commission: d.commission_forecast,
  }));

  // Heatmap data — group by week (7 days) for the calendar grid
  const heatmap = useMemo(() => {
    const days = data.days || [];
    const max = Math.max(1, ...days.map((d) => d.commission || 0));
    return days.map((d) => ({ ...d, intensity: (d.commission || 0) / max }));
  }, [data]);

  const grandTotalLast90 = history.reduce((s, d) => s + (d.commission || 0), 0);

  return (
    <div>
      <PageHeader
        eyebrow="INTELLIGENCE"
        title="Earnings Calendar & Forecast"
        subtitle="Visualize your last 90 days of commissions and a 30-day projection based on your recent trend."
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard label="Avg Daily" value={`৳${(data.summary?.avg_daily || 0).toLocaleString()}`} icon={TrendingUp} accent="emerald" />
        <StatCard label="Next 30 days" value={`৳${(data.summary?.next_30d || 0).toLocaleString()}`} icon={Sparkles} accent="gold" />
        <StatCard label="Last 90 days" value={`৳${grandTotalLast90.toLocaleString()}`} icon={BarChart3} accent="amber" />
        <StatCard label="Tracked Days" value={history.length} icon={CalendarDays} accent="blue" />
      </div>

      <div className="p-glass mb-4" style={{ padding: 18 }}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-black text-white">Daily commission — last 90 days</div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">history</div>
        </div>
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <AreaChart data={history}>
              <defs>
                <linearGradient id="goldArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22c073" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#22c073" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }} interval="preserveStartEnd" minTickGap={20} />
              <Tooltip contentStyle={{ background: "#0a0d18", border: "1px solid rgba(34, 192, 115, 0.35)", borderRadius: 8, fontSize: 12 }} formatter={(v) => `৳${Number(v).toLocaleString()}`} />
              <Area type="monotone" dataKey="commission" stroke="#a7f3d0" strokeWidth={2} fill="url(#goldArea)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="p-glass mb-4" style={{ padding: 18 }}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-black text-white">Forecast — next 30 days</div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300">projection</div>
        </div>
        <div style={{ width: "100%", height: 200 }}>
          <ResponsiveContainer>
            <BarChart data={forecast}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }} interval={2} />
              <Tooltip contentStyle={{ background: "#0a0d18", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 8, fontSize: 12 }} formatter={(v) => `৳${Number(v).toLocaleString()}`} />
              <Bar dataKey="commission" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="p-glass" style={{ padding: 18 }}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-black text-white">Earnings Heatmap</div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-200">activity</div>
        </div>
        <div className="grid gap-1" style={{ gridTemplateColumns: "repeat(15, minmax(0, 1fr))" }}>
          {heatmap.map((d) => {
            const alpha = 0.06 + 0.85 * (d.intensity || 0);
            return (
              <div
                key={d.date}
                title={`${d.date} — ৳${(d.commission || 0).toLocaleString()}`}
                className="aspect-square rounded"
                style={{
                  background: `rgba(212,175,55,${alpha})`,
                  border: "1px solid rgba(34, 192, 115, 0.18)",
                }}
              />
            );
          })}
        </div>
        <div className="flex items-center gap-2 mt-3 text-[10px] text-white/45 font-bold uppercase tracking-[0.18em]">
          <span>less</span>
          <div className="flex gap-1">
            {[0.1, 0.25, 0.45, 0.7, 1].map((a) => (
              <div key={a} className="w-4 h-4 rounded" style={{ background: `rgba(212,175,55,${a})`, border: "1px solid rgba(34, 192, 115, 0.18)" }} />
            ))}
          </div>
          <span>more</span>
        </div>
      </div>
    </div>
  );
}
